import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateFilterType } from '@core/enum/date';
import { TimeHandler } from '@core/services/time-handler.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { CalendarModule } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { OverlayModule } from 'primeng/overlay';
import { ButtonComponent } from '../button/button.component';
import { currentDateByTimeZone } from '@feature/marketing-campaign/utils';

@Component({
  selector: 'app-custom-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OverlayModule,
    CalendarModule,
    ButtonComponent,
    TranslateModule,
    InputTextModule,
    FloatLabelModule
  ],
  templateUrl: './custom-calendar.component.html',
  styleUrl: './custom-calendar.component.scss'
})
export class CustomCalendarComponent implements OnChanges {
  @Input() title = '';
  @Input() endDateBehavior: 'UNLIMITED' | 'SAME_AS_START' = 'UNLIMITED';
  @Input() maxDate?: Date = undefined;
  @Input() minDate?: Date = undefined;
  @Input() filterTypeInit?: DateFilterType = undefined;
  @Input() isInput: boolean = false;
  @Input() datesSelected: Array<Date | undefined> = [];
  @Input() appendTo: 'body' | HTMLElement | undefined = undefined;
  @Input() required: boolean = false;
  @Input() isHasAllTime: boolean = false;
  @Input() isBulkActionsLog: boolean = false;
  @Input() DF = [
    { value: DateFilterType.TODAY },
    { value: DateFilterType.YESTERDAY },
    { value: DateFilterType.LAST_7_DAYS },
    { value: DateFilterType.LAST_14_DAYS },
    { value: DateFilterType.THIS_WEEK },
    { value: DateFilterType.LAST_WEEK },
    { value: DateFilterType.THIS_MONTH },
    { value: DateFilterType.LAST_MONTH },
    { value: DateFilterType.THIS_YEAR },
    { value: DateFilterType.LAST_YEAR },
    { value: DateFilterType.MAXIMUM },
    { value: DateFilterType.CUSTOM }
  ];
  @Input() timeZoneName?: string = '';
  @Input() isTaskFilter: boolean = false;

  @Output() datesSelectedChange = new EventEmitter<Array<Date | undefined>>();
  @Output() filterTypeInitChange = new EventEmitter<DateFilterType>();

  visible = false;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  rangeDates: Array<Date | undefined> = [];
  th = inject(TimeHandler);
  translateService = inject(TranslateService);

  DFActive?: DateFilterType;
  DFSelected?: DateFilterType;

  get valueCalendar() {
    if (this.DFActive) {
      return this.DFActive
        ? this.translateService.instant('primeng.' + this.DFActive) + `(${this.rangeDateToString()})`
        : this.rangeDateToString();
    } else {
      return this.rangeDateToString();
    }
  }

  ngOnInit() {
    if (this.isHasAllTime) {
      this.DF.push({ value: DateFilterType.ALL_TIME });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['filterTypeInit']?.currentValue && this.filterTypeInit) {
      this.DFActive = this.filterTypeInit;
      this.filterDate(this.filterTypeInit);
    } else {
      if (changes?.['datesSelected']?.currentValue?.length === 0) {
        this.dateFrom = undefined;
        this.dateTo = undefined;
        this.rangeDates = [];
        this.DFSelected = undefined;
        this.DFActive = undefined;
      }
    }

    if (changes?.['timeZoneName']?.currentValue) {
      this.maxDate = currentDateByTimeZone(changes?.['timeZoneName']?.currentValue);
    }
  }

  cancelDateFilter() {
    this.visible = false;

    this.rangeDates = [...this.datesSelected];
    this.dateTo = this.rangeDates[0];
    this.dateFrom = this.rangeDates[1];

    this.DFSelected = this.DFActive;
  }

  applyDateFilter() {
    this.visible = false;

    const [start, end] = this.rangeDates || [];
    let finalStart = start;
    let finalEnd = end;

    if (this.endDateBehavior === 'SAME_AS_START' && start && !end) {
      finalEnd = start;
      this.dateTo = start;
      this.rangeDates = [start, start];
    }

    const shouldEmitDates = this.isHasAllTime || this.rangeDates?.length === 2;
    if (shouldEmitDates) {
      this.datesSelected = [finalStart, finalEnd];
      this.datesSelectedChange.emit(this.datesSelected);
    }

    this.DFActive = this.DFSelected;
  }

  rangeDateToString(): string {
    const dateArr = this.datesSelected.filter(date => !!date).map(date => this.th.getDateValue(date as Date));

    const [startDate, endDate] = dateArr;

    if (dateArr.length === 2) {
      if (startDate === endDate) {
        return `${startDate}`;
      } else {
        return `${startDate} - ${endDate}`;
      }
    }

    if (dateArr.length === 1) {
      return `${startDate} - No end date`;
    }

    return '';
  }

  getDateFilterArray(dateArr: Date[] | undefined[]) {
    [this.dateFrom, this.dateTo] = dateArr;
  }

  setDateFilter() {
    if (!this.dateFrom && !this.dateTo) return;
    if (!this.dateFrom) {
      this.dateFrom = this.dateTo;
      this.dateTo = undefined;
    }
    if (!this.dateTo) {
      this.dateTo = this.dateFrom;
    }
    if (this.dateTo! < this.dateFrom!) this.dateTo = undefined;
    this.rangeDates = [this.dateFrom || this.dateTo, this.dateTo];
  }

  filterDate(value: DateFilterType) {
    this.DFSelected = value;
    let date1, date2: Date | undefined;

    const date = this.timeZoneName ? moment(currentDateByTimeZone(this.timeZoneName)).format() : moment().format();

    switch (value) {
      case DateFilterType.LAST_7_DAYS:
        date1 = moment(date).subtract(7, 'day').startOf('date').toDate();
        date2 = moment(date).subtract(1, 'day').endOf('date').toDate();
        break;
      case DateFilterType.LAST_14_DAYS:
        date1 = moment(date).subtract(15, 'day').startOf('date').toDate();
        date2 = moment(date).subtract(1, 'day').endOf('date').toDate();
        break;
      case DateFilterType.LAST_28_DAYS:
        date1 = moment(date).subtract(28, 'day').startOf('date').toDate();
        date2 = moment(date).endOf('date').toDate();
        break;
      case DateFilterType.TODAY:
        date1 = moment(date).startOf('date').toDate();
        date2 = moment(date).endOf('date').toDate();

        break;
      case DateFilterType.YESTERDAY:
        date1 = moment(date).subtract(1, 'day').startOf('date').toDate();
        date2 = moment(date).subtract(1, 'day').endOf('date').toDate();
        break;
      case DateFilterType.THIS_WEEK:
        date1 = moment(date).startOf('week').toDate();
        date2 = moment(date).endOf('week').toDate();
        break;
      case DateFilterType.LAST_WEEK:
        date1 = moment(date).subtract(1, 'weeks').startOf('week').toDate();
        date2 = moment(date).subtract(1, 'weeks').endOf('week').toDate();
        break;
      case DateFilterType.THIS_MONTH:
        date1 = moment(date).startOf('month').toDate();
        date2 = moment(date).endOf('month').toDate();
        break;
      case DateFilterType.LAST_MONTH:
        date1 = moment(date).subtract(1, 'months').startOf('month').toDate();
        date2 = moment(date).subtract(1, 'months').endOf('month').toDate();
        break;
      case DateFilterType.THIS_YEAR:
        date1 = moment(date).startOf('year').toDate();
        date2 = moment(date).endOf('year').toDate();
        break;
      case DateFilterType.LAST_YEAR:
        date1 = moment(date).subtract(1, 'years').startOf('year').toDate();
        date2 = moment(date).subtract(1, 'years').endOf('year').toDate();
        break;
      case DateFilterType.ALL_TIME:
        // Maximum Date value according to Mozilla specification

        date1 = undefined;
        date2 = undefined;
        break;
      case DateFilterType.MAXIMUM:
        date1 = moment(date).subtract(37, 'month').startOf('date').toDate();
        date2 = moment(date).endOf('date').toDate();
        break;
      case DateFilterType.CUSTOM:
        date1 = moment(date).startOf('date').toDate();
        date2 = moment(date).endOf('date').toDate();
        break;
    }

    if (this.isHasAllTime) {
      this.rangeDates = [date1, date2].filter(item => !!item);
    } else {
      this.rangeDates = [date1, date2];
    }

    [this.dateFrom, this.dateTo] = [date1, date2];

    this.filterTypeInit = value;
    this.filterTypeInitChange.emit(this.filterTypeInit);
  }

  onSelect() {
    this.DFSelected = DateFilterType.CUSTOM;
  }

  onToggleVisible() {
    this.visible = !this.visible;
  }

  onInputDate() {
    this.DFSelected = DateFilterType.CUSTOM;
  }
}
